import { getVariants } from '../../lib/imageVariants'

type Props = {
  src?: string
  alt: string
  className?: string
  /** CSS width the image occupies, for the sizes attribute. e.g. "76px" or "(min-width:1024px) 387px, 90vw" */
  sizes?: string
  width?: number
  height?: number
  loading?: 'eager' | 'lazy'
  /** true only for the element that is the LCP candidate on its page */
  priority?: boolean
  onError?: React.ReactEventHandler<HTMLImageElement>
  'aria-hidden'?: boolean | 'true' | 'false'
}

/**
 * Drop-in replacement for <img> that serves AVIF/WebP from the build-time variant
 * map when one exists, and falls through to a plain <img> when it does not
 * (remote URLs such as img.youtube.com, or API-supplied images we do not ship).
 *
 * Always emits width/height so the browser can reserve layout space.
 */
export function SmartImage({
  src, alt, className, sizes, width, height,
  loading = 'lazy', priority = false, onError,
  'aria-hidden': ariaHidden,
}: Props) {
  const v = getVariants(src)
  const eager = priority || loading === 'eager'
  const common = {
    alt,
    className,
    width,
    height,
    decoding: 'async' as const,
    onError,
    'aria-hidden': ariaHidden,
    ...(eager ? { loading: 'eager' as const } : { loading: 'lazy' as const }),
    // fetchpriority is not in React 18's prop types; pass it through lowercase.
    ...(priority ? ({ fetchpriority: 'high' } as Record<string, string>) : {}),
  }

  if (!v || v.widths.length === 0) {
    return <img src={src} {...common} />
  }

  const srcset = (map: Record<number, string>) =>
    v.widths.filter(w => map[w]).map(w => `${map[w]} ${w}w`).join(', ')

  const largest = v.widths[v.widths.length - 1]
  const fallbackSrc = v.fallback[largest] ?? v.fallback[v.widths[0]] ?? src

  return (
    <picture>
      {Object.keys(v.avif).length > 0 && (
        <source type="image/avif" sizes={sizes} srcSet={srcset(v.avif)} />
      )}
      {Object.keys(v.webp).length > 0 && (
        <source type="image/webp" sizes={sizes} srcSet={srcset(v.webp)} />
      )}
      <img src={fallbackSrc} sizes={sizes} srcSet={srcset(v.fallback)} {...common} />
    </picture>
  )
}
